import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { translations } from '@/translations/translations';

type T = typeof translations.fr.admin.themeConfig;

interface Props {
  title: string;
  description: string;
  examplesText: string;
  isSaving: boolean;
  t: T;
  onTitleChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onExamplesChange: (v: string) => void;
  onSave: () => void;
  onClear: () => void;
}

export function ManualThemeForm({ title, description, examplesText, isSaving, t, onTitleChange, onDescriptionChange, onExamplesChange, onSave, onClear }: Props) {
  return (
    <section className="pt-7">

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
        <div className="space-y-5">
          <Field label={t.manualForm.themeTitle}>
            <input
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder={t.manualForm.themeTitlePlaceholder}
              className={controlClass}
            />
          </Field>

          <Field label={t.manualForm.description}>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={t.manualForm.descriptionPlaceholder}
              rows={3}
              className={cn(textareaClass, "resize-none")}
            />
          </Field>

          <Field label={t.manualForm.examples}>
            <textarea
              value={examplesText}
              onChange={(e) => onExamplesChange(e.target.value)}
              placeholder={t.manualForm.examplesPlaceholder}
              rows={6}
              className={cn(textareaClass, "resize-none font-mono text-xs leading-6")}
            />
          </Field>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-border/40 bg-muted/10 p-5">
            <p className="flex items-center gap-2 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Icon name="info" className="h-3 w-3" />
              {t.excelForm.excelFormatRequired}
            </p>
            <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand/50" />
                <span><span className="font-semibold text-foreground/80">{t.manualForm.themeTitle}</span> : {t.manualForm.themeTitlePlaceholder}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand/50" />
                <span><span className="font-semibold text-foreground/80">{t.manualForm.description}</span> : {t.manualForm.descriptionPlaceholder}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand/50" />
                <span><span className="font-semibold text-foreground/80">{t.manualForm.examples}</span> : {t.manualForm.examplesLabel}</span>
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-border/40 bg-muted/10 p-5">
            <p className="flex items-center gap-2 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Icon name="fileText" className="h-3 w-3" />
              {t.manualForm.examplesLabel}
            </p>
            <p className="mt-3 whitespace-pre-line text-xs leading-5 text-muted-foreground/[0.72]">{t.manualForm.examplesPlaceholder}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-end gap-3 pt-5">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-transparent px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <Icon name="x" className="h-3.5 w-3.5" />
          {t.manualForm.clear}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving || !title.trim()}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand px-5 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand disabled:border-border/40 disabled:bg-muted/20 disabled:text-muted-foreground"
        >
          {isSaving ? (
            <span className="mr-1.5 h-4 w-4 animate-spin rounded-full border-2 border-slate-950 border-t-transparent" />
          ) : (
            <Icon name="save" className="h-3.5 w-3.5" />
          )}
          {isSaving ? t.manualForm.saving : t.manualForm.save}
        </button>
      </div>
    </section>
  );
}

const controlClass =
  "h-10 w-full rounded-xl border border-border/40 bg-muted/20 px-3.5 text-sm text-foreground shadow-none placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-brand/45";

const textareaClass =
  "w-full rounded-xl border border-border/40 bg-muted/20 px-3.5 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-brand/45";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
